import React, { useCallback, useEffect, useState } from "react";
import { BellRing, CheckCircle2, ExternalLink, Loader2, Save, Upload } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { PageHeader } from "@/components/patterns";
import { ErrorState } from "@/components/patterns/States";
import { cn } from "@/lib/utils";
import { dateLocale, withClock } from "@/lib/dateTime";

type Account = {
  project_id: string;
  client_email: string;
  web_config: Record<string, string>;
  vapid_key: string;
  enabled: boolean;
  connection_status: "CONNECTED" | "ERROR";
  last_error: string | null;
  last_checked_at: string | null;
};
type Settings = { configured: boolean; account: Account | null; devices: number; last_24h: { sent: number; failed: number; pending: number; skipped: number } };

type LogRow = { id: string; created_on: string; person: string | null; title: string; kind: string | null; result: "SENT" | "FAILED" | "SKIPPED" | "PENDING"; attempts: number; sent_to: number; last_error: string | null };
const RESULT: Record<LogRow["result"], { label: string; tone: string }> = {
  SENT: { label: "Sent", tone: "bg-success-soft text-success" },
  PENDING: { label: "Waiting", tone: "bg-warning-soft text-warning" },
  FAILED: { label: "Failed", tone: "bg-danger-soft text-danger" },
  SKIPPED: { label: "Not sent", tone: "bg-muted text-muted-foreground" },
};
const VIEWS = [["all", "All"], ["problems", "Problems"], ["waiting", "Waiting"], ["sent", "Sent"]] as const;
const when = (iso: string) => new Date(iso).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }));

const WEB_FIELDS = ["apiKey", "authDomain", "projectId", "messagingSenderId", "appId"] as const;
const WEB_LABELS: Record<(typeof WEB_FIELDS)[number], string> = { apiKey: "Web key", authDomain: "Sign-in address", projectId: "Project ID", messagingSenderId: "Sender ID", appId: "App ID" };

/** Reads the firebaseConfig snippet Firebase shows ({ apiKey: "…", authDomain: "…", … }) into its values */
function parseSnippet(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const m of text.matchAll(/([A-Za-z]+)\s*:\s*["']([^"']+)["']/g)) out[m[1]!] = m[2]!;
  return out;
}

/** The latest pushes and what happened to each: the first place to look when someone says "I didn't get it" */
function RecentPushes() {
  const [view, setView] = useState<(typeof VIEWS)[number][0]>("all");
  const [rows, setRows] = useState<LogRow[] | null>(null);
  const [find, setFind] = useState("");
  const load = useCallback(async () => {
    try {
      setRows(unwrapObject<{ rows: LogRow[] }>(await api.get(`/api/Push/Log?view=${view}&hours=72`)).rows);
    } catch {
      setRows([]);
    }
  }, [view]);
  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);
  const shown = (rows || []).filter((r) => !find.trim() || `${r.person || ""} ${r.title} ${r.kind || ""}`.toLowerCase().includes(find.trim().toLowerCase()));
  return (
    <Card className="shadow-xs">
      <CardHeader>
        <CardTitle className="text-base font-semibold">Recent pushes</CardTitle>
        <CardDescription>The last 3 days. Use it to answer "why didn't I get it?": who it was for, what kind, and what happened. The text of the notification is not shown.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Show" className="flex overflow-hidden rounded-lg border border-border">
            {VIEWS.map(([k, label]) => (
              <button key={k} type="button" aria-pressed={view === k} onClick={() => setView(k)} className={cn("h-8 px-3 text-xs", view === k ? "bg-sidebar font-semibold text-white" : "bg-card text-muted-foreground hover:text-foreground")}>{label}</button>
            ))}
          </div>
          <Input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find a person or kind" aria-label="Find a person or kind" className="h-8 max-w-[240px] text-xs" />
          <Button type="button" variant="outline" size="sm" onClick={() => void load()}>Refresh</Button>
        </div>
        {rows === null ? (
          <p className="py-4 text-center text-sm text-muted-foreground">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">{view === "problems" ? "No problems in the last 3 days." : "Nothing here yet."}</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full min-w-[640px] border-collapse text-[12.5px]">
              <thead>
                <tr className="bg-panel text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-semibold">When</th>
                  <th className="px-3 py-2 font-semibold">Person</th>
                  <th className="px-3 py-2 font-semibold">What</th>
                  <th className="px-3 py-2 font-semibold">Result</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} className="border-t border-border/70 align-top">
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-[11.5px] text-muted-foreground">{when(r.created_on)}</td>
                    <td className="px-3 py-2">{r.person || "Someone"}</td>
                    <td className="px-3 py-2"><span className="block">{r.title}</span><span className="block font-mono text-[11px] text-muted-foreground">{r.kind || ""}</span></td>
                    <td className="px-3 py-2">
                      <span className={cn("inline-flex h-[22px] items-center rounded-md px-2 text-[11.5px] font-semibold", RESULT[r.result].tone)}>{RESULT[r.result].label}{r.result === "SENT" ? ` to ${r.sent_to}` : ""}</span>
                      {r.last_error && <span className="mt-0.5 block text-[11.5px] text-muted-foreground">{r.last_error}</span>}
                      {r.result === "PENDING" && r.attempts > 0 && <span className="mt-0.5 block text-[11.5px] text-muted-foreground">Tried {r.attempts} time{r.attempts === 1 ? "" : "s"}, trying again</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Settings → Push notifications: the Firebase project this CRM sends pushes through */
export function PushSettingsPage() {
  const { success, error } = useToast();
  const [data, setData] = useState<Settings | null>(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [keyText, setKeyText] = useState("");
  const [keyName, setKeyName] = useState("");
  const [snippet, setSnippet] = useState("");
  const [web, setWeb] = useState<Record<string, string>>({});
  const [vapid, setVapid] = useState("");
  const [enabled, setEnabled] = useState(true);

  const fill = useCallback((s: Settings) => {
    setData(s);
    setWeb(s.account?.web_config || {});
    setVapid(s.account?.vapid_key || "");
    setEnabled(s.account?.enabled ?? true);
    setKeyText("");
    setKeyName("");
  }, []);

  const load = useCallback(async () => {
    try {
      setFailed(false);
      fill(unwrapObject<Settings>(await api.get("/api/Push/Settings")));
    } catch {
      setFailed(true);
    }
  }, [fill]);
  useEffect(() => {
    void load();
  }, [load]);

  const pickFile = async (f: File | undefined) => {
    if (!f) return;
    if (f.size > 20000) return error("That file is too big", "The key file is a few kilobytes. Check you chose the right one.");
    setKeyText(await f.text());
    setKeyName(f.name);
  };

  const readSnippet = (text: string) => {
    setSnippet(text);
    const got = parseSnippet(text);
    setWeb((w) => ({ ...w, ...Object.fromEntries(WEB_FIELDS.filter((k) => got[k]).map((k) => [k, got[k]!])) }));
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    if (!keyText.trim() && !data.configured) return error("Missing the key file", "Upload the key file from your notification service.");
    setSaving(true);
    try {
      fill(unwrapObject<Settings>(await api.post("/api/Push/Settings", { ...(keyText.trim() ? { service_account_json: keyText } : {}), web_config: web, vapid_key: vapid.trim(), enabled })));
      success("Push notifications saved", "The notification service accepted the setup");
    } catch (err: any) {
      error("Couldn't save", err?.message || "Check the details and try again");
    } finally {
      setSaving(false);
    }
  };

  const recheck = async () => {
    setChecking(true);
    try {
      fill(unwrapObject<Settings>(await api.post("/api/Push/TestConnection", {})));
      success("Connected", "The notification service accepts the saved setup");
    } catch (err: any) {
      error("The notification service didn't accept it", err?.message || "Try again");
      await load();
    } finally {
      setChecking(false);
    }
  };

  if (failed) return <ErrorState title="Couldn't load the push settings" onRetry={load} />;
  if (!data) return <FormSkeleton />;
  const acc = data.account;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageHeader title="Push notification setup" />
      <p className="text-sm text-muted-foreground">Pop-ups on people's phones and computers, even when the CRM is closed, sent through your own notification service account (free). Nothing leaves the CRM except the pop-up text.</p>

      {acc && (
        <Card className="shadow-xs">
          <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-5 text-sm">
            <span className={`inline-flex items-center gap-1.5 font-medium ${acc.connection_status === "CONNECTED" ? "text-success" : "text-destructive"}`}>
              {acc.connection_status === "CONNECTED" ? <CheckCircle2 className="size-4" /> : null}
              {acc.connection_status === "CONNECTED" ? (acc.enabled ? "Connected" : "Connected, switched off") : "The notification service isn't accepting it"}
            </span>
            <span className="text-muted-foreground">Project <strong className="font-mono text-foreground">{acc.project_id}</strong></span>
            <span className="text-muted-foreground">{data.devices} device{data.devices === 1 ? "" : "s"} signed up</span>
            <span className="text-muted-foreground">Last 24 h: {data.last_24h.sent} sent{data.last_24h.failed ? `, ${data.last_24h.failed} failed` : ""}{data.last_24h.pending ? `, ${data.last_24h.pending} waiting` : ""}</span>
            <Button type="button" variant="outline" size="sm" onClick={recheck} disabled={checking} className="ml-auto">{checking ? <Loader2 className="size-3.5 animate-spin" /> : null} Check again</Button>
            {acc.last_error && <p className="w-full text-xs text-destructive">{acc.last_error}</p>}
          </CardContent>
        </Card>
      )}

      {data.configured && <RecentPushes />}

      <form onSubmit={save} className="space-y-4">
        <Card className="shadow-xs">
          <CardHeader>
            <CardTitle className="text-base font-semibold">1 · Notification service key</CardTitle>
            <CardDescription>
              Open your notification service's dashboard and create a project (or pick yours). Go to ⚙ Project settings → Service accounts → Generate new private key, and upload the downloaded file here. It is kept safely and never shown again.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-input bg-card px-3 py-2 text-sm hover:bg-muted">
                <Upload className="size-4" /> Choose the key file
                <input type="file" accept=".json,application/json" className="sr-only" onChange={(e) => void pickFile(e.target.files?.[0])} />
              </label>
              <span className="text-sm text-muted-foreground">{keyName ? `Ready to save: ${keyName}` : acc ? `Saved key: ${acc.client_email}` : "No file chosen"}</span>
            </div>
            <p className="text-xs text-muted-foreground">Sending messages must be switched on for the project (it is on for new projects).</p>
          </CardContent>
        </Card>

        <Card className="shadow-xs">
          <CardHeader>
            <CardTitle className="text-base font-semibold">2 · Website setup code</CardTitle>
            <CardDescription>In Project settings → General → Your apps, add a Web app (the &lt;/&gt; icon) and copy the setup code it shows. Paste it below and the boxes fill in.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea value={snippet} onChange={(e) => readSnippet(e.target.value)} rows={4} placeholder="Paste the setup code here" aria-label="Paste the setup code" className="font-mono text-xs" />
            <div className="grid gap-3 sm:grid-cols-2">
              {WEB_FIELDS.map((k) => (
                <div key={k} className="space-y-1.5">
                  <Label htmlFor={`fb-${k}`}>{WEB_LABELS[k]}</Label>
                  <Input id={`fb-${k}`} value={web[k] || ""} onChange={(e) => setWeb((w) => ({ ...w, [k]: e.target.value.trim() }))} className="font-mono text-xs" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xs">
          <CardHeader>
            <CardTitle className="text-base font-semibold">3 · Browser key</CardTitle>
            <CardDescription>Project settings → Cloud Messaging → Web Push certificates → Generate key pair. Paste the long public key.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="fb-vapid">Key pair</Label>
              <Input id="fb-vapid" value={vapid} onChange={(e) => setVapid(e.target.value.trim())} className="font-mono text-xs" />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5">
              <div className="text-sm"><div className="font-medium">Send push notifications</div><div className="text-xs text-muted-foreground">Switch off to pause pushes without losing the setup</div></div>
              <Switch checked={enabled} onCheckedChange={setEnabled} aria-label="Send push notifications" />
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={saving}>{saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save and check the connection</Button>
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><BellRing className="size-3.5" /> People are asked once, after signing in, and choose what they get in Settings → Notifications.</span>
        </div>
      </form>
    </div>
  );
}
